import type { LessonCompleteRequest, LessonCompleteResponse } from "../../types/newtypes";
import type { CompletionIdentity } from "../../store/api/completionRequest";

export const RECOVERY_KEY = "pedagogue.lessonCompletionRecovery.v1";
export const RECOVERY_TTL = 24 * 60 * 60 * 1000;
export const RECOVERY_MAX_BYTES = 64 * 1024;

export interface CompletionAttempt extends CompletionIdentity {
  version: 1;
  attemptId: string;
  lessonId: string;
  operation: "complete" | "skip";
  phase: "pending" | "saved";
  wasReview: boolean;
  createdAt: number;
  expiresAt: number;
  data?: LessonCompleteRequest;
  lastFailureStatus?: 401;
}

const validId = (value: unknown): value is string =>
  typeof value === "string" && /^[a-zA-Z0-9_-]{1,128}$/.test(value);

export function parseCompletionAttempt(raw: string | null, now = Date.now()): CompletionAttempt | null {
  if (!raw || new Blob([raw]).size > RECOVERY_MAX_BYTES) {return null;}
  try {
    const value = JSON.parse(raw);
    const allowed = ["version", "attemptId", "lessonId", "operation", "phase", "wasReview", "createdAt", "expiresAt", "data", "userId", "organisationId", "lastFailureStatus"];
    if (!value || typeof value !== "object" || Object.keys(value).some((key) => !allowed.includes(key)) ||
      value.version !== 1 || !validId(value.attemptId) || !validId(value.lessonId) || !validId(value.userId) ||
      !(value.organisationId === null || validId(value.organisationId)) ||
      !["complete", "skip"].includes(value.operation) || !["pending", "saved"].includes(value.phase) ||
      (value.lastFailureStatus !== undefined && (value.lastFailureStatus !== 401 || value.phase !== "pending")) ||
      typeof value.wasReview !== "boolean" || !Number.isFinite(value.createdAt) ||
      value.createdAt > now + 60_000 || value.expiresAt !== value.createdAt + RECOVERY_TTL || value.expiresAt <= now) {return null;}
    if (value.phase === "pending") {
      const data = value.data;
      if (!data || Object.keys(data).some((key) => !["card_scores", "local_date", "local_offset_minutes", "time_zone"].includes(key)) ||
        !Array.isArray(data.card_scores) || data.card_scores.some((score: {card_id?: unknown; score?: unknown; mistakes?: unknown}) =>
        !score || Object.keys(score).some((key) => !["card_id", "score", "mistakes"].includes(key)) ||
          !validId(score.card_id) || typeof score.score !== "number" || !Number.isFinite(score.score) || score.score < 0 ||
          typeof score.mistakes !== "number" || !Number.isInteger(score.mistakes) || score.mistakes < 0) ||
        (data.local_date !== undefined && !/^\d{4}-\d{2}-\d{2}$/.test(data.local_date)) ||
        (data.local_offset_minutes !== undefined && (!Number.isFinite(data.local_offset_minutes) || Math.abs(data.local_offset_minutes) > 1440)) ||
        (data.time_zone !== undefined && (typeof data.time_zone !== "string" || data.time_zone.length > 100))) {return null;}
    } else if (value.data !== undefined) {return null;}
    return value as CompletionAttempt;
  } catch { return null; }
}

export function readCompletionAttempt(): CompletionAttempt | null {
  try {
    const raw = sessionStorage.getItem(RECOVERY_KEY);
    const value = parseCompletionAttempt(raw);
    if (raw && !value) {sessionStorage.removeItem(RECOVERY_KEY);}
    return value;
  } catch { return null; }
}

export function persistCompletionAttempt(attempt: CompletionAttempt): boolean {
  try {
    const raw = JSON.stringify(attempt);
    if (!parseCompletionAttempt(raw)) {return false;}
    sessionStorage.setItem(RECOVERY_KEY, raw);
    return true;
  } catch { return false; }
}

export function clearCompletionAttempt(attemptId: string): void {
  try {
    const value = readCompletionAttempt();
    if (value?.attemptId === attemptId) {sessionStorage.removeItem(RECOVERY_KEY);}
  } catch { /* Memory-only recovery is still usable while the page remains open. */ }
}

// The scoped request validates the live profile before and after the operation.
// Retained progress may keep its historical organisation, which is not its auth principal.
export function confirmsAttempt(response: LessonCompleteResponse, attempt: CompletionAttempt): boolean {
  return Boolean(response && validId(response.id) && response.is_completed === true &&
    response.lesson_id === attempt.lessonId && response.user_id === attempt.userId);
}

export interface CompletionSaveError {
  kind: "retry" | "sign-in" | "restricted" | "identity";
  message: string;
  retryAt?: number;
}

export interface CompletionApiError {
  status?: number | string;
  data?: { error?: string; error_code?: string; message?: string; retry_after?: string };
}

export function asCompletionError(error: unknown): CompletionApiError {
  return error && typeof error === "object" ? error as CompletionApiError : {};
}

export function completionSaveError(cause: unknown, now = Date.now()): CompletionSaveError {
  const error = asCompletionError(cause);
  if (error?.data?.error === "COMPLETION_IDENTITY_CHANGED") {return {
    kind: "identity", message: "This attempt belongs to a different account or organisation and cannot be submitted.",
  };}
  if (error?.status === 401) {return {
    kind: "sign-in", message: "Sign in to save your completed attempt. It will stay in this tab while you sign in.",
  };}
  if (error?.status === 403 || error?.status === 423) {return {
    kind: "restricted", message: "Saving this unit is currently restricted. Your completed attempt is retained. Check your course access before trying again.",
  };}
  if (error?.status === 429) {
    const header = error?.data?.retry_after;
    const seconds = typeof header === "string" && /^\d+$/.test(header) ? Number(header) * 1000 : NaN;
    const date = typeof header === "string" ? Date.parse(header) : NaN;
    return { kind: "retry", message: "Saving is temporarily busy. Your completed attempt is retained. Please wait before trying again.",
      retryAt: now + (Number.isFinite(seconds) ? seconds : Number.isFinite(date) ? Math.max(0, date - now) : 30_000) };
  }
  return { kind: "retry", message: "We couldn't confirm that this unit saved. Your completed attempt is still here. Please try saving again." };
}
